import React, { useEffect, useRef } from 'react';

// Text tabs styled like the navbar: the active one is white and underlined.
// Follows the tabs pattern: only the selected tab is a Tab stop; arrow keys
// (wrapping), Home and End move focus and selection. The page renders the panel
// with id `${idPrefix}-panel` and aria-labelledby={tabId(idPrefix, value)}.
export const tabId = (idPrefix, value) => `${idPrefix}-tab-${value}`;

const TextTabs = ({ tabs, value, onChange, label, idPrefix, className = '' }) => {
    const tabRefs = useRef([]);
    const listRef = useRef(null);

    // When the row scrolls sideways, keep the selected tab fully in view (without scrolling the page)
    useEffect(() => {
        const list = listRef.current;
        const tab = tabRefs.current[tabs.findIndex((t) => t.value === value)];
        if (!list || !tab) return;
        const left = tab.offsetLeft;
        const right = left + tab.offsetWidth;
        if (left < list.scrollLeft) list.scrollLeft = left;
        else if (right > list.scrollLeft + list.clientWidth) list.scrollLeft = right - list.clientWidth;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleKeyDown = (event, index) => {
        const last = tabs.length - 1;
        const next = {
            ArrowRight: index === last ? 0 : index + 1,
            ArrowLeft: index === 0 ? last : index - 1,
            Home: 0,
            End: last,
        }[event.key];
        if (next === undefined) return;

        event.preventDefault();
        onChange(tabs[next].value);
        tabRefs.current[next].focus();
    };

    return (
        // Scrolls sideways when the tabs don't fit; the divider is an inset shadow so scrolling doesn't clip it.
        // The page column around it needs min-w-0, or it grows to fit the tabs instead
        <div ref={listRef} role='tablist' aria-label={label} className={`relative flex overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_#27272a] ${className}`}>
            {tabs.map((tab, index) => {
                const active = tab.value === value;
                return (
                    <button
                        key={tab.value}
                        ref={(el) => { tabRefs.current[index] = el; }}
                        id={tabId(idPrefix, tab.value)}
                        type='button'
                        role='tab'
                        aria-selected={active}
                        aria-controls={`${idPrefix}-panel`}
                        tabIndex={active ? 0 : -1}
                        onClick={() => onChange(tab.value)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        style={{ fontFamily: 'inherit' }} // buttons don't inherit the page font by default
                        className={`flex-1 shrink-0 py-3 px-3 bg-transparent cursor-pointer border-solid border-0 border-b-2 uppercase tracking-wider text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${active
                            ? 'text-white border-white'
                            : 'text-zinc-500 border-transparent hover:text-zinc-200'}`}
                    >
                        {tab.label}
                        {tab.count !== undefined &&
                            <span className={`ml-1.5 font-normal ${active ? 'text-zinc-400' : 'text-zinc-600'}`}>{tab.count}</span>
                        }
                    </button>
                );
            })}
        </div>
    );
};

export default TextTabs
